import React, { Component } from 'react';
import PropTypes from 'prop-types';
import lodashUniqBy from 'lodash/fp/uniqBy';

import typeCheck from 'common/util/typeCheck';

import {
  FEED_CATEGORY_LIST,
  FEED_CATEGORY_BY_ID,
  FEED_CATEGORY_SELF_TOUR,
} from 'common/feedCategoryConstants';

import configureDecoratedComponent from 'client/helpers/configureDecoratedComponent';
import withApiState from 'client/helpers/withApiState';
import branch from 'client/core/branch';
import log from 'client/lib/log';

const debug = log.debug('sunroom:feedCategoryHelpers');

function getInitialFeedCategoryState() {
  return {
    count: 12,
    page: 0,
    total: 0,
    isFullyLoaded: false,
    listings: [],
  };
}

export function findFeedCategoryId(_feedCategoryId) {
  for (const feedCategoryId of FEED_CATEGORY_LIST) {
    if (feedCategoryId.toLowerCase() === _feedCategoryId.toLowerCase()) {
      return feedCategoryId;
    }
  }
  return null;
}

export function actionInitializeFeedCategoryState({ state }, { feedCategoryId }) {
  if (!state.exists(['feedCategory', 'byId', feedCategoryId])) {
    debug(`Inializing feed category "${feedCategoryId}"`);
    state.set(['feedCategory', 'byId', feedCategoryId], getInitialFeedCategoryState());
  }
}

export async function actionFeedCategoryReset({ state }, { feedCategoryId }) {
  state.set(['feedCategory', 'byId', feedCategoryId], getInitialFeedCategoryState());
  state.commit();
}

export function actionInitializeFeedCategoryList({ state }) {
  debug('Inializing feed categories ...');
  for (const feedCategoryId of FEED_CATEGORY_LIST) {
    actionInitializeFeedCategoryState({ state }, { feedCategoryId });
  }
}

export function actionResetFeedCategoryList({ state }) {
  debug('Resetting feed categories ...');
  for (const feedCategoryId of FEED_CATEGORY_LIST) {
    actionFeedCategoryReset({ state }, { feedCategoryId });
  }
}

export function actionGetFeedCategory({ state }, { feedCategoryId }) {
  typeCheck('feedCategoryId::NonEmptyString', feedCategoryId);

  const feedCategoryState = state.get(['feedCategory', 'byId', feedCategoryId]);
  const feedCategory = FEED_CATEGORY_BY_ID[feedCategoryId];

  return {
    feedCategoryState,
    feedCategory,
  };
}

export async function actionFeedCategoryLoadlistings(
  { state, apiClient },
  { feedCategoryId, mapArea, onSuccess }
) {
  const { feedCategoryState, feedCategory } = actionGetFeedCategory({ state }, { feedCategoryId });
  const isMapMode = !!mapArea;

  if (feedCategoryState.isFullyLoaded && !isMapMode) {
    return;
  }

  const currentUser = state.get(['currentUser']);
  const currentUserMarket = state.get(['markets', 'currentUserMarket']);
  const renterProfilesByUserId = state.get(['household', 'renterProfilesByUserId']);

  const payload = {
    page: feedCategoryState.page,
    count: feedCategoryState.count,
    filters: typeof feedCategory.filters !== 'function' ? {} : feedCategory.filters({
      user: currentUser,
      market: currentUserMarket,
      renterProfile: renterProfilesByUserId && renterProfilesByUserId[currentUser.id],
    }),
    sort: [],
  };

  if (isMapMode) {
    payload.page = 0;
    payload.count = 96;
    payload.filters.locationPointTwo = mapArea;
  }

  if (currentUser.sessionCount === 1) {
    payload.sort.push(['pinned', 'desc']);
  }

  debug(`Loading listings for feed category "${feedCategoryId}": page=${payload.page}, count=${payload.count}`); // eslint-disable-line max-len

  await apiClient.postWithState({
    state: state,
    action: feedCategory.action,
    queryId: feedCategory.id,
    payload: payload,
    onSuccess({ data, total }) {
      if (onSuccess) {
        onSuccess({ data, total });
      }
      else {
        const feedCategoryCursor = state.select(['feedCategory', 'byId', feedCategoryId]);
        const records = lodashUniqBy('id', [...feedCategoryCursor.get('listings'), ...data]);

        feedCategoryCursor.set('listings', records);
        feedCategoryCursor.set('isFullyLoaded', data.length < payload.count);
        feedCategoryCursor.set('total', total);
        feedCategoryCursor.set('page', payload.page + 1);
      }
    },
  });
}

export function withFeedCategory(getFeedCategoryParamsFromProps) {
  typeCheck('definition::Function | NonEmptyObject', getFeedCategoryParamsFromProps);

  getFeedCategoryParamsFromProps = typeof getFeedCategoryParamsFromProps === 'function'
    ? getFeedCategoryParamsFromProps
    : () => getFeedCategoryParamsFromProps;

  return function feedCategoryDecorator(ComponentToDecorate) {
    @withApiState((props) => {
      const params = getFeedCategoryParamsFromProps(props);
      const feedCategory = FEED_CATEGORY_BY_ID[params.feedCategoryId];

      return {
        feedCategoryApiState: {
          action: feedCategory.action,
          queryId: feedCategory.id,
        },
      };
    })
    @branch((props) => {
      const { feedCategoryId } = getFeedCategoryParamsFromProps(props);

      return {
        listings: ['feedCategory', 'byId', feedCategoryId, 'listings'],
        total: ['feedCategory', 'byId', feedCategoryId, 'total'],
        isFullyLoaded: ['feedCategory', 'byId', feedCategoryId, 'isFullyLoaded'],
        listingPhotoResolution: ['listingPhotoResolution'],
        selfShowingFeedCategoryV1: [
          'analytics',
          'experiments',
          'activeVariants',
          'selfShowingFeedCategoryV1',
        ],
      };
    })
    class WithFeedCategory extends Component {
      static propTypes = {
        dispatch: PropTypes.func.isRequired,
        selfShowingFeedCategoryV1: PropTypes.string.isRequired,
      }

      componentWillUnmount() {
        const { dispatch } = this.props;
        const params = getFeedCategoryParamsFromProps(this.props);
        const feedCategory = FEED_CATEGORY_BY_ID[params.feedCategoryId];
        if (feedCategory.refreshOnUnmount) {
          dispatch(actionFeedCategoryReset, {
            feedCategoryId: params.feedCategoryId,
          });
        }
      }

      render() {
        const {
          dispatch,
          feedCategoryId,
          feedCategoryApiState,
          listings,
          total,
          isFullyLoaded,
          listingPhotoResolution,
          selfShowingFeedCategoryV1,
        } = this.props;
        const params = getFeedCategoryParamsFromProps(this.props);
        const feedCategory = FEED_CATEGORY_BY_ID[params.feedCategoryId];
        let feedCategoryTitle = feedCategory.title;
        if (params.feedCategoryId === FEED_CATEGORY_SELF_TOUR) {
          feedCategoryTitle = selfShowingFeedCategoryV1 === 'selfTour'
            ? 'Self Tour 🔑'
            : 'Tour on your own 🔑';
        }

        return (
          <ComponentToDecorate
            {...this.props}
            {...feedCategory}
            {...feedCategoryApiState}
            title={feedCategoryTitle}
            listings={listings || []}
            total={total || 0}
            dispatch={dispatch}
            feedCategoryId={feedCategoryId}
            isFullyLoaded={isFullyLoaded || false}
            listingPhotoResolution={listingPhotoResolution}
          />
        );
      }
    }

    configureDecoratedComponent({
      DecoratedComponent: WithFeedCategory,
      OriginalComponent: ComponentToDecorate,
      displayNameSuffix: 'WithFeedCategory',
    });

    return WithFeedCategory;
  };
}

export function shouldFeedCategoryLoadInitialListings({ state }, { feedCategoryId }) {
  const { feedCategoryState } = actionGetFeedCategory({ state }, { feedCategoryId });

  return (
    feedCategoryState.listings.length === 0
    && !feedCategoryState.isFullyLoaded
  );
}
